/**
 * PDF Scale Utilities
 *
 * Single source of truth for architectural scale on exported PDFs: the
 * render-time px/m for a chosen scale, the placement math that sizes an
 * image so it prints at exactly that scale, and the ruler that labels it.
 *
 * The invariant: a ruler may only describe the scale the image was PLACED
 * at. Callers size with `sizeImageToExactScale` and draw the ruler from the
 * `scale` it returns — never from the scale they asked the renderer for.
 *
 * Units (see reference-docs/pdf-export/scale.md):
 * - logical (CSS) px  — renderer native unit; CSS_PX_PER_INCH logical px = 1"
 * - backing-store px  — canvas.width; logical × devicePixelRatio
 * - captured px       — dom-to-image output; logical × CAPTURE_SCALE
 * Every pixel count handed to this module must be LOGICAL.
 */

export const CSS_PX_PER_INCH = 96;
export const FEET_PER_METER = 3.28084;
export const MM_PER_INCH = 25.4;

/** Draw red 1" ticks along page edges for print verification. Dev-only. */
export const DEBUG_SCALE_RULERS =
  process.env.REACT_APP_PDF_DEBUG_RULERS === 'true';

// Preferred architectural scales (clean fractions only, no 3/x scales)
export const PREFERRED_SCALES = [
  { inchesPerFoot: 1, ratio: '1', description: '1" = 1\'' },
  { inchesPerFoot: 0.5, ratio: '1/2', description: '1/2" = 1\'' },
  { inchesPerFoot: 0.25, ratio: '1/4', description: '1/4" = 1\'' },
  { inchesPerFoot: 0.125, ratio: '1/8', description: '1/8" = 1\'' },
  { inchesPerFoot: 0.0625, ratio: '1/16', description: '1/16" = 1\'' },
  { inchesPerFoot: 0.03125, ratio: '1/32', description: '1/32" = 1\'' },
];

const SMALLEST_SCALE = PREFERRED_SCALES[PREFERRED_SCALES.length - 1];

/**
 * Logical px per foot a renderer must use so that CSS_PX_PER_INCH logical
 * px print as one inch at the given scale.
 */
export const pixelsPerFootForScale = (scale) =>
  CSS_PX_PER_INCH * scale.inchesPerFoot;

/** Logical px per meter for a scale (the `initialScale` / `previewScale` unit). */
export const pixelsPerMeterForScale = (scale) =>
  pixelsPerFootForScale(scale) * FEET_PER_METER;

/**
 * Logical size of a canvas whose backing store was scaled by devicePixelRatio
 * (as `updateCanvasSize` does). Use this — never `canvas.width` — for scale math.
 */
export const logicalCanvasSize = (canvas) => {
  const dpr = window.devicePixelRatio || 1;
  return { width: canvas.width / dpr, height: canvas.height / dpr };
};

/**
 * Largest preferred scale at which a feetW × feetH extent fits in
 * maxWidthInches × maxHeightInches. Falls back to the smallest scale.
 */
export const selectLargestFittingScale = (
  feetW,
  feetH,
  maxWidthInches,
  maxHeightInches
) => {
  for (const scale of PREFERRED_SCALES) {
    if (
      feetW * scale.inchesPerFoot <= maxWidthInches &&
      feetH * scale.inchesPerFoot <= maxHeightInches
    ) {
      return scale;
    }
  }
  return SMALLEST_SCALE;
};

/**
 * Select the closest preferred architectural scale for a given inches-per-foot value.
 */
export const selectPreferredArchitecturalScale = (actualInchesPerFoot) => {
  let closestScale = SMALLEST_SCALE;
  let smallestDiff = Infinity;

  for (const scale of PREFERRED_SCALES) {
    const diff = Math.abs(scale.inchesPerFoot - actualInchesPerFoot);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestScale = scale;
    }
  }

  return closestScale;
};

/**
 * Size an image so it prints at an exact preferred scale: the largest one at
 * which the WHOLE image fits the available space.
 *
 * @param {number} pixelsPerFoot - Logical px per foot the image was rendered at
 * @param {number} imageWidthPixels - Image width in LOGICAL px
 * @param {number} imageHeightPixels - Image height in LOGICAL px
 * @param {number} maxWidthInches - Available width on the page, inches
 * @param {number} maxHeightInches - Available height on the page, inches
 * @returns {{ width: number, height: number, scale: Object }} printed size in inches + the placed scale
 */
export const sizeImageToExactScale = (
  pixelsPerFoot,
  imageWidthPixels,
  imageHeightPixels,
  maxWidthInches,
  maxHeightInches
) => {
  const imageFeetWidth = imageWidthPixels / pixelsPerFoot;
  const imageFeetHeight = imageHeightPixels / pixelsPerFoot;

  const scale = selectLargestFittingScale(
    imageFeetWidth,
    imageFeetHeight,
    maxWidthInches,
    maxHeightInches
  );

  return {
    width: imageFeetWidth * scale.inchesPerFoot,
    height: imageFeetHeight * scale.inchesPerFoot,
    scale,
  };
};

/** Printed size (inches) of an image placed at a given scale. */
export const sizeImageAtScale = (
  pixelsPerFoot,
  imageWidthPixels,
  imageHeightPixels,
  scale
) => ({
  width: (imageWidthPixels / pixelsPerFoot) * scale.inchesPerFoot,
  height: (imageHeightPixels / pixelsPerFoot) * scale.inchesPerFoot,
  scale,
});

/**
 * One scale for a set of images that share a sheet (e.g. every elevation of a
 * room): the largest preferred scale at which EVERY image fits its cell.
 * @param {Array<{pixelsPerFoot:number, imageWidth:number, imageHeight:number}>} images - logical px
 */
export const selectCommonFittingScale = (
  images,
  maxWidthInches,
  maxHeightInches
) => {
  let common = PREFERRED_SCALES[0];
  for (const img of images) {
    const s = selectLargestFittingScale(
      img.imageWidth / img.pixelsPerFoot,
      img.imageHeight / img.pixelsPerFoot,
      maxWidthInches,
      maxHeightInches
    );
    if (s.inchesPerFoot < common.inchesPerFoot) common = s;
  }
  return common;
};

/**
 * Pick the page orientation that lets a drawing print at the larger
 * preferred scale (ties → landscape). `available` gives the usable inches
 * for each orientation, after margins/header/footer.
 *
 * @param {number} pixelsPerFoot - logical px/ft of the image
 * @param {number} imageWidthPixels - logical px
 * @param {number} imageHeightPixels - logical px
 * @param {{landscape: {w, h}, portrait: {w, h}}} available - inches
 * @returns {'landscape' | 'portrait'}
 */
export const selectPageOrientation = (
  pixelsPerFoot,
  imageWidthPixels,
  imageHeightPixels,
  available
) => {
  const at = (o) =>
    sizeImageToExactScale(
      pixelsPerFoot,
      imageWidthPixels,
      imageHeightPixels,
      available[o].w,
      available[o].h
    ).scale.inchesPerFoot;
  return at('portrait') > at('landscape') ? 'portrait' : 'landscape';
};

/** Same as `sizeImageToExactScale` but in mm, for jsPDF docs using `unit: 'mm'`. */
export const sizeImageToExactScaleMm = (
  pixelsPerFoot,
  imageWidthPixels,
  imageHeightPixels,
  maxWidthMm,
  maxHeightMm
) => {
  const r = sizeImageToExactScale(
    pixelsPerFoot,
    imageWidthPixels,
    imageHeightPixels,
    maxWidthMm / MM_PER_INCH,
    maxHeightMm / MM_PER_INCH
  );
  return {
    width: r.width * MM_PER_INCH,
    height: r.height * MM_PER_INCH,
    scale: r.scale,
  };
};

const formatFeetLabel = (feet) =>
  feet === Math.floor(feet) ? `${Math.round(feet)}'` : `${feet.toFixed(1)}'`;

/**
 * Draw a 2" architectural scale ruler with tick marks and feet labels.
 * Matches the iOS ArchitecturalScaleRuler implementation.
 *
 * @param {jsPDF} pdf - The PDF document
 * @param {{inchesPerFoot: number, description: string}} scale - the PLACED scale
 * @param {number} x - Right edge X (ruler draws leftward), in the pdf's unit
 * @param {number} y - Baseline Y (ruler draws upward), in the pdf's unit
 * @param {{unit: 'in' | 'mm' | 'pt'}} options - must match the pdf's unit
 */
export const PDF_UNITS_PER_INCH = { in: 1, mm: MM_PER_INCH, pt: 72 };

export const drawScaleRuler = (pdf, scale, x, y, { unit }) => {
  const inch = PDF_UNITS_PER_INCH[unit];
  if (!inch) throw new Error(`drawScaleRuler: unknown unit ${unit}`);
  const rulerWidth = 2 * inch;
  const rulerHeight = 0.15 * inch;
  const feetPerInch = 1 / scale.inchesPerFoot;
  const is16thScale = scale.inchesPerFoot <= 1 / 16;

  const startX = x - rulerWidth;
  const baselineY = y;
  const topY = baselineY - rulerHeight;

  pdf.setDrawColor(50, 50, 50);
  pdf.setLineWidth(0.008 * inch);

  // Horizontal baseline
  pdf.line(startX, baselineY, startX + rulerWidth, baselineY);

  // Full-height ticks at 0", 1", 2"
  for (let i = 0; i <= 2; i++) {
    const tx = startX + i * inch;
    pdf.line(tx, topY, tx, baselineY);
  }

  const drawTicks = (fractions, denominator, heightFraction) => {
    const h = rulerHeight * heightFraction;
    for (const i of fractions) {
      const tx = startX + (i * inch) / denominator;
      pdf.line(tx, baselineY - h, tx, baselineY);
    }
  };
  drawTicks([1, 3], 2, 1 / 2);
  drawTicks([1, 3, 5, 7], 4, 1 / 3);
  drawTicks([1, 3, 5, 7, 9, 11, 13, 15], 8, 1 / 4);
  if (is16thScale) {
    const sixteenths = [];
    for (let i = 1; i < 32; i += 2) sixteenths.push(i);
    drawTicks(sixteenths, 16, 1 / 5);
  }

  // Feet labels above 1" and 2" ticks
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6);
  pdf.setTextColor(50, 50, 50);
  const labelY = topY - 0.04 * inch;
  pdf.text(formatFeetLabel(feetPerInch), startX + inch, labelY, {
    align: 'center',
  });
  pdf.text(formatFeetLabel(feetPerInch * 2), startX + 2 * inch, labelY, {
    align: 'center',
  });

  // Scale description text below ruler
  pdf.setFontSize(7);
  pdf.text(`Scale: ${scale.description}`, x, baselineY + 0.15 * inch, {
    align: 'right',
  });

  // Reset
  pdf.setDrawColor(0, 0, 0);
  pdf.setTextColor(0, 0, 0);
};

/** Ruler for a pdf created with `unit: 'in'`. */
export const drawScaleRulerInches = (pdf, scale, x, y) =>
  drawScaleRuler(pdf, scale, x, y, { unit: 'in' });

/** Ruler for a pdf created with `unit: 'mm'`. */
export const drawScaleRulerMm = (pdf, scale, xMm, yMm) =>
  drawScaleRuler(pdf, scale, xMm, yMm, { unit: 'mm' });

/**
 * Draw debug 1-inch interval tick marks along all page edges.
 * Works with any PDF unit system by accepting the page dimensions and
 * the size of one inch in those units.
 *
 * @param {jsPDF} pdf - The PDF document
 * @param {number} pageWidth - Page width in current PDF units
 * @param {number} pageHeight - Page height in current PDF units
 * @param {number} oneInch - Size of 1 inch in current PDF units (1 for inches, 25.4 for mm)
 */
export const drawDebugEdgeRulers = (pdf, pageWidth, pageHeight, oneInch) => {
  const tickLength = 0.25 * oneInch;
  const totalTicksX = Math.floor(pageWidth / oneInch);
  const totalTicksY = Math.floor(pageHeight / oneInch);

  pdf.setDrawColor(255, 0, 0);
  pdf.setLineWidth(0.01 * oneInch);

  // Top and bottom edges
  for (let i = 0; i <= totalTicksX; i++) {
    const x = i * oneInch;
    pdf.line(x, 0, x, tickLength);
    pdf.line(x, pageHeight, x, pageHeight - tickLength);
  }

  // Left and right edges
  for (let i = 0; i <= totalTicksY; i++) {
    const y = i * oneInch;
    pdf.line(0, y, tickLength, y);
    pdf.line(pageWidth, y, pageWidth - tickLength, y);
  }

  // Reset
  pdf.setDrawColor(0, 0, 0);
};
